import type { Calendar } from 'src/meetings/models';
import { CalendarAccountIcon } from './CalendarAccountIcon';
import { ActionRow } from 'src/meetings/components/ActionRow';
import React from 'react';
import { useDeleteCalendarMutation } from 'src/meetings/hooks/useDeleteCalendarMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type Props = {
  calendar: Calendar;
  onDisconnected: () => Promise<unknown>;
  disabled?: boolean;
};

export const ConnectedCalendarAccountCard = ({ calendar, onDisconnected }: Props) => {
  const { deleteCalendar, isLoading: isDeletingCalendar } = useDeleteCalendarMutation();

  const handleDeleteCalendar = async (id: string) => {
    try {
      await deleteCalendar(id);
      await onDisconnected();
    } catch (error) {
      notifyAndLogError(
        'Failed to disconnect calendar',
        'An error occurred while trying to disconnect the calendar. Please try again later.',
        error,
        { calendarId: id },
      );
    }
  };

  if (calendar.status === 'CONNECTING') {
    return (
      <ActionRow
        icon={<CalendarAccountIcon platform={calendar.platform} />}
        title={'Connecting...'}
        button={{
          onClick: () => {},
          variant: 'primary',
          disabled: true,
          loading: true,
        }}
      />
    );
  }

  if (calendar.status === 'CONNECTED') {
    return (
      <ActionRow
        icon={<CalendarAccountIcon platform={calendar.platform} />}
        title={calendar.emailAddress}
        button={{
          label: 'Disconnect',
          onClick: () => handleDeleteCalendar(calendar.id),
          variant: 'secondary',
          loading: isDeletingCalendar,
        }}
      />
    );
  }

  if (calendar.status === 'DISCONNECTED') {
    return (
      <ActionRow
        icon={<CalendarAccountIcon platform={calendar.platform} />}
        title={calendar.emailAddress ?? 'Unknown email address'}
        button={{
          label: 'Remove',
          onClick: () => handleDeleteCalendar(calendar.id),
          variant: 'primary',
          loading: isDeletingCalendar,
        }}
      />
    );
  }

  return null;
};
